import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "../types/database";
import { clearSession, getStoredSession, storeSession } from "../lib/auth";

// Session configuration
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of inactivity
const TOKEN_EXPIRY_MS = 55 * 60 * 1000; // 55 minutes (token expires at 60 min)

// Error codes as per Specifiche_Integrazione_TDH.md
export const AUTH_ERROR_CODES = {
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  SESSION_TIMEOUT: "SESSION_TIMEOUT",
  USER_NOT_AUTHORIZED: "USER_NOT_AUTHORIZED",
} as const;

// Error messages as per Specifiche_Integrazione_TDH.md
export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  [AUTH_ERROR_CODES.TOKEN_EXPIRED]: "La sessione è scaduta. Effettua nuovamente l'accesso dal Portale Operatori.",
  [AUTH_ERROR_CODES.SESSION_TIMEOUT]: "Sessione scaduta per inattività. Effettua nuovamente l'accesso.",
  [AUTH_ERROR_CODES.USER_NOT_AUTHORIZED]: "Non hai i permessi per accedere a questo servizio.",
};

interface AuthState {
  user: User | null;
  token: string | null;
  tokenExpiresAt: number | null;
  lastActivity: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;

  // Actions
  setUser: (user: User, token: string, expiresIn?: number) => void;
  logout: (errorCode?: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null, errorCode?: string | null) => void;
  initFromStorage: () => void;
  updateActivity: () => void;
  checkSessionValidity: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      tokenExpiresAt: null,
      lastActivity: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      errorCode: null,

      setUser: (user: User, token: string, expiresIn?: number) => {
        const now = Date.now();
        const expiresAt = expiresIn
          ? now + (expiresIn * 1000)
          : now + TOKEN_EXPIRY_MS;

        storeSession(user, token, expiresIn);
        set({
          user,
          token,
          tokenExpiresAt: expiresAt,
          lastActivity: now,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          errorCode: null,
        });
      },

      logout: (errorCode?: string) => {
        clearSession();
        // If errorCode provided, set appropriate message per Specifiche_Integrazione_TDH.md
        const errorMessage = errorCode ? AUTH_ERROR_MESSAGES[errorCode] || null : null;
        set({
          user: null,
          token: null,
          tokenExpiresAt: null,
          lastActivity: null,
          isAuthenticated: false,
          isLoading: false,
          error: errorMessage,
          errorCode: errorCode || null,
        });
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null, errorCode?: string | null) => set({
        error,
        errorCode: errorCode ?? null,
        isLoading: false
      }),

      initFromStorage: () => {
        const stored = getStoredSession();
        if (stored && stored.user && stored.token) {
          // Check if session is still valid
          const now = Date.now();
          const storedExpiry = stored.tokenExpiresAt || (now + TOKEN_EXPIRY_MS);
          const storedActivity = stored.lastActivity || now;

          // Check if token expired
          if (now > storedExpiry) {
            clearSession();
            set({
              isLoading: false,
              error: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.TOKEN_EXPIRED],
              errorCode: AUTH_ERROR_CODES.TOKEN_EXPIRED,
            });
            return;
          }

          // Check if user inactive (30 min)
          if ((now - storedActivity) > SESSION_TIMEOUT_MS) {
            clearSession();
            set({
              isLoading: false,
              error: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.SESSION_TIMEOUT],
              errorCode: AUTH_ERROR_CODES.SESSION_TIMEOUT,
            });
            return;
          }

          set({
            user: stored.user,
            token: stored.token,
            tokenExpiresAt: storedExpiry,
            lastActivity: now,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            errorCode: null,
          });
        } else {
          set({ isLoading: false });
        }
      },

      updateActivity: () => {
        const { isAuthenticated } = get();
        if (isAuthenticated) {
          set({ lastActivity: Date.now() });
        }
      },

      checkSessionValidity: () => {
        const { tokenExpiresAt, lastActivity, isAuthenticated, logout } = get();
        if (!isAuthenticated) return false;

        const now = Date.now();
        const isDev = import.meta.env.DEV;

        // Check inactivity first
        if (lastActivity && (now - lastActivity) > SESSION_TIMEOUT_MS) {
          if (isDev) console.log("[Auth] Session timeout due to inactivity");
          logout(AUTH_ERROR_CODES.SESSION_TIMEOUT);
          return false;
        }

        // Check token expiry
        if (tokenExpiresAt && now > tokenExpiresAt) {
          if (isDev) console.log("[Auth] Token expired");
          logout(AUTH_ERROR_CODES.TOKEN_EXPIRED);
          return false;
        }

        return true;
      },
    }),
    {
      name: "dpm-auth",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        tokenExpiresAt: state.tokenExpiresAt,
        lastActivity: state.lastActivity,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
